import { Telemetry } from '@ctw/shared/context/telemetry';
import { tw } from '@ctw/shared/utils/tailwind';
import { Theme as RadixUiTheme } from '@radix-ui/themes';
import { uniqueId } from 'lodash-es';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { type NavigateOptions, useLocation, useNavigate } from 'react-router';

export type RouterOptions = NavigateOptions;

export type ZusService = 'zap-iframe' | 'zap-sof' | 'zap-standalone';

export const ZapVersions = ['v1', 'v2'] as const;
export type ZapVersion = (typeof ZapVersions)[number];

export const isZapVersion = (ZuiVersion: string | null | undefined): ZuiVersion is ZapVersion =>
  ZapVersions.includes(ZuiVersion as never);

type ZuiPresence =
  | 'legacyMainNavigationBar'
  | 'mainNavigationBar'
  | 'subNavigationBar'
  | 'dockedDrawer'
  | 'loadingSpinner';

type ZuiPresences = {
  [key in ZuiPresence]: boolean;
};

type ZuiPresenceRegistrations = {
  [key in ZuiPresence]: Array<string>;
};

const defaultZuiPresences: ZuiPresences = {
  dockedDrawer: false,
  legacyMainNavigationBar: false,
  mainNavigationBar: false,
  subNavigationBar: false,
  loadingSpinner: false,
} as const;

const defaultZuiPresenceRegistrations: ZuiPresenceRegistrations = {
  dockedDrawer: [],
  legacyMainNavigationBar: [],
  mainNavigationBar: [],
  subNavigationBar: [],
  loadingSpinner: [],
} as const;

// Presence state lives at module scope so `usePresence` can subscribe directly via
// `useSyncExternalStore` without going through React context, which means a change to
// a single presence key only re-renders subscribers of that hook, not every `useZuiContext()`
// consumer.
const presenceState: ZuiPresences = { ...defaultZuiPresences };
const presenceRegistrations: ZuiPresenceRegistrations = { ...defaultZuiPresenceRegistrations };
const presenceEventTarget = new EventTarget();
const PRESENCE_EVENT = 'change';

const registerPresence = (presence: ZuiPresence): (() => void) => {
  const presenceId = uniqueId();
  presenceRegistrations[presence].push(presenceId);

  if (presenceRegistrations[presence].length === 1) {
    presenceState[presence] = true;
    presenceEventTarget.dispatchEvent(new Event(PRESENCE_EVENT));
  }

  return () => {
    const index = presenceRegistrations[presence].indexOf(presenceId);
    if (index !== -1) {
      presenceRegistrations[presence].splice(index, 1);
    }

    if (presenceRegistrations[presence].length === 0) {
      presenceState[presence] = false;
      presenceEventTarget.dispatchEvent(new Event(PRESENCE_EVENT));
    }
  };
};

const subscribeToPresenceChanges = (callback: () => void) => {
  presenceEventTarget.addEventListener(PRESENCE_EVENT, callback);
  return () => presenceEventTarget.removeEventListener(PRESENCE_EVENT, callback);
};

type ZuiEventType = 'beforeNavigate' | 'afterNavigate' | 'beforeUrlChange' | 'afterUrlChange';

interface ZuiEventPayloads extends Record<ZuiEventType, Record<string, unknown>> {
  beforeNavigate: {
    from: {
      path: string;
      search: URLSearchParams;
    };
    to: {
      path: string;
      search: URLSearchParams;
    };
  };
  afterNavigate: {
    from: {
      path: string;
      search: URLSearchParams;
    };
    to: {
      path: string;
      search: URLSearchParams;
    };
  };
  beforeUrlChange: {
    from: {
      path: string;
      search: URLSearchParams;
    };
    to: {
      path: string;
      search: URLSearchParams;
    };
  };
  afterUrlChange: {
    from: {
      path: string;
      search: URLSearchParams;
    };
    to: {
      path: string;
      search: URLSearchParams;
    };
  };
}

interface ZuiEvent<EventName extends ZuiEventType = ZuiEventType> {
  name: ZuiEventType;
  payload: ZuiEventPayloads[EventName];
}

interface ZuiState {
  service: string;
  navigate: (path: string, routerOptions?: RouterOptions) => void;
  onZuiEvent: <TEventName extends ZuiEventType>(event: ZuiEvent<TEventName>) => void;
  registerZuiEventListener: <TEventName extends ZuiEventType>(
    eventName: TEventName,
    handler: (event: ZuiEvent<TEventName>) => void,
  ) => () => void;
  isNavigating: boolean;
  pathname: string;
}

export const ZuiContext = createContext<ZuiState | null>(null);

export const useZuiContext = () => {
  const context = useContext(ZuiContext);

  if (!context) {
    throw new Error('useZuiContext must be used within a ZuiProvider');
  }

  return context;
};

/**
 * Register that a presence is on-screen until the comsuming component is
 * unmounted, or when `enabled` is set to `false`.
 */
export const useRegisterPresence = (presence: ZuiPresence, enabled = true): void => {
  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }
    return registerPresence(presence);
  }, [presence, enabled]);
};

export const usePresence = (presence: ZuiPresence): boolean => {
  const getSnapshot = useCallback(() => presenceState[presence], [presence]);
  return useSyncExternalStore(subscribeToPresenceChanges, getSnapshot, () => false);
};

// Only used to parse root-relative paths into a pathname and search params.
const URL_PARSING_BASE = 'http://localhost';

/**
 * Resolves `href` to `pathname + search`, carrying each of `persistentParams` over from
 * `currentSearch` unless `href` sets it. The hash is dropped. Throws if `href` is not a valid URL.
 */
export const withPersistentQueryParams = (
  href: string,
  currentSearch: string,
  persistentParams: Array<string> = [],
): string => {
  const requestedUrl = new URL(href, URL_PARSING_BASE);
  const currentSearchParams = new URLSearchParams(currentSearch);
  const destinationUrl = new URL(requestedUrl.pathname, URL_PARSING_BASE);

  for (const param of persistentParams) {
    const value = currentSearchParams.get(param);
    if (value) {
      destinationUrl.searchParams.set(param, value);
    }
  }

  for (const [param, value] of requestedUrl.searchParams.entries()) {
    destinationUrl.searchParams.set(param, value);
  }

  return `${destinationUrl.pathname}${destinationUrl.search}`;
};

interface ZuiProviderProps extends PropsWithChildren {
  service: ZusService | 'storybook' | 'playwright';
  navigatePersistentQueryParams?: Array<string>;
}

export const ZuiProvider = ({
  service,
  navigatePersistentQueryParams,
  children,
}: ZuiProviderProps) => {
  const routerNavigate = useNavigate();
  const { pathname, search } = useLocation();
  const latestLocationRef = useRef({ pathname, search });
  latestLocationRef.current = { pathname, search };
  const [isNavigating, setIsNavigating] = useState(false);
  const isNavigatingRef = useRef(false);
  const pendingNavigationRef = useRef<{
    payload: ZuiEventPayloads['afterNavigate'];
    triggerNavigationEvents: boolean;
  } | null>(null);
  const eventListenersRef = useRef<Record<ZuiEventType, Array<(event: ZuiEvent) => void>>>({
    beforeNavigate: [],
    afterNavigate: [],
    beforeUrlChange: [],
    afterUrlChange: [],
  });

  const onZuiEvent = useCallback((event: ZuiEvent) => {
    for (const listener of eventListenersRef.current[event.name]) {
      listener(event);
    }
  }, []);

  const registerZuiEventListener: ZuiState['registerZuiEventListener'] = useCallback(
    (eventName, listener) => {
      const registeredListener = listener as (event: ZuiEvent) => void;
      eventListenersRef.current[eventName] = [
        ...eventListenersRef.current[eventName],
        registeredListener,
      ];

      return () => {
        eventListenersRef.current[eventName] = eventListenersRef.current[eventName].filter(
          (existingListener) => existingListener !== registeredListener,
        );
      };
    },
    [],
  );

  // Include search so search-only navigations also trigger the completion effect.
  const resolvedUrl = `${pathname}${search}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `resolvedUrl` is intended to re-run this effect on every URL change.
  useEffect(() => {
    const pending = pendingNavigationRef.current;
    if (!pending) {
      return;
    }

    pendingNavigationRef.current = null;
    isNavigatingRef.current = false;

    onZuiEvent({
      name: 'afterUrlChange',
      payload: pending.payload,
    });

    if (pending.triggerNavigationEvents) {
      Telemetry.afterNavigate();
      onZuiEvent({
        name: 'afterNavigate',
        payload: pending.payload,
      });
      setIsNavigating(false);
    }
  }, [resolvedUrl]);

  const zuiContext: ZuiState = useMemo(
    () => ({
      service,
      navigate: (requestedPath, routerOptions) => {
        // Use a ref for guarding navigation to avoid stale closures and to prevent recreating the
        // context on every navigation
        if (isNavigatingRef.current) {
          return;
        }

        const { pathname: pathnameBeforeNavigation, search: searchStringBeforeNavigation } =
          latestLocationRef.current;

        let destinationPath: string;
        try {
          destinationPath = withPersistentQueryParams(
            requestedPath,
            searchStringBeforeNavigation,
            navigatePersistentQueryParams,
          );
        } catch {
          // Failure to parse `requestedPath` means the path was invalid, so we can't navigate to it
          return;
        }

        // Disallow navigating to the current URL (same pathname and search params, includes the `?` prefix).
        if (destinationPath === `${pathnameBeforeNavigation}${searchStringBeforeNavigation}`) {
          return;
        }

        const destinationUrl = new URL(destinationPath, URL_PARSING_BASE);
        const destinationPathname = destinationUrl.pathname;

        // Only trigger navigation events for real page changes; this ensures that we don't trigger
        // navigation events for things like opening a drawer with a deep link, which would cause
        // unnecessary UI churn and potentially extra loading indicators.
        const triggerNavigationEvents = destinationPathname !== pathnameBeforeNavigation;

        const navigateEventPayload = {
          from: {
            path: pathnameBeforeNavigation,
            search: new URLSearchParams(searchStringBeforeNavigation),
          },
          to: {
            path: destinationPathname,
            search: new URLSearchParams(destinationUrl.search),
          },
        };

        onZuiEvent({
          name: 'beforeUrlChange',
          payload: navigateEventPayload,
        });

        // Trigger the `beforeNavigate` event and set the `isNavigating` state to `true`
        if (triggerNavigationEvents) {
          onZuiEvent({
            name: 'beforeNavigate',
            payload: navigateEventPayload,
          });
          isNavigatingRef.current = true;
          setIsNavigating(true);
        }

        Telemetry.beforeNavigate();

        // Store the pending navigation so the pathname-change useEffect can complete it.
        // Navigation completion is detected reactively when the router updates the pathname
        // (via useLocation in React Router).
        pendingNavigationRef.current = {
          payload: navigateEventPayload,
          triggerNavigationEvents,
        };

        // Perform the actual navigation
        routerNavigate(destinationPath, routerOptions);
        latestLocationRef.current = {
          pathname: destinationPathname,
          search: destinationUrl.search,
        };
      },
      onZuiEvent,
      registerZuiEventListener,
      isNavigating,
      pathname,
    }),
    [
      service,
      isNavigating,
      pathname,
      navigatePersistentQueryParams,
      routerNavigate,
      onZuiEvent,
      registerZuiEventListener,
    ],
  );

  return (
    // https://www.radix-ui.com/themes/docs/components/theme
    <RadixUiTheme
      className={tw`flex h-[100vh] max-h-[100vh] w-[100vw] max-w-[100vw] flex-col overflow-hidden`}
      hasBackground={false}
      appearance="light"
      panelBackground="translucent"
      radius="medium"
      scaling="100%"
      accentColor="green"
      grayColor="gray"
    >
      <ZuiContext.Provider value={zuiContext}>{children}</ZuiContext.Provider>
    </RadixUiTheme>
  );
};
